import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { TrendingUp, ArrowUpRight, Truck, Send } from "lucide-react";

const PLATFORM_LABEL = { twitter: "X", facebook: "FB", instagram: "IG" };

function ShippingReminderCard() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const preview = async () => {
    try {
      const { data: r } = await api.post("/admin/shipping/reminder-blast", null, {
        params: { dry_run: true },
      });
      setData(r);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    preview();
  }, []);

  const send = async () => {
    if (!data || data.total === 0) return;
    if (
      !window.confirm(
        `Send a reminder email to ${data.total} artist${data.total === 1 ? "" : "s"}? They'll get a list of their listings missing shipping prices plus a dashboard link.`,
      )
    ) {
      return;
    }
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/shipping/reminder-blast");
      toast.success(`Reminder emails: ${r.sent} sent · ${r.failed} failed`);
      setData(r);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reminders");
    } finally {
      setSending(false);
    }
  };

  if (error || !data) return null;
  const empty = data.total === 0;

  return (
    <section
      data-testid="shipping-reminder-card"
      className="mt-12 border border-[#E5E0D8] bg-white"
    >
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#E5E0D8]">
        <div className="flex items-center gap-3">
          <Truck className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} />
          <div>
            <p className="label-eyebrow text-[#1E3A8A]">— Catalogue health</p>
            <h2 className="font-serif-display text-xl tracking-tight font-medium mt-1 text-[#111]">
              Artists missing shipping prices
            </h2>
          </div>
        </div>
        {!empty && (
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="shipping-reminder-send"
            className="label-eyebrow bg-[#1E3A8A] text-white hover:bg-[#162a66] px-5 py-3 inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? "Sending…" : `Email ${data.total} ${data.total === 1 ? "artist" : "artists"}`}
          </button>
        )}
      </header>
      {empty ? (
        <p className="px-6 py-8 text-sm text-[#1B5E20]" data-testid="shipping-reminder-empty">
          ✓ Every active artist has set shipping prices on all their listings. Nothing to remind.
        </p>
      ) : (
        <ul className="divide-y divide-[#E5E0D8]">
          {data.recipients.map((r) => (
            <li
              key={r.artist_id}
              data-testid={`shipping-reminder-row-${r.artist_id}`}
              className="px-6 py-3 flex items-center justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <p className="font-serif-display text-base text-[#111] truncate">{r.name}</p>
                <p className="font-mono-aam text-xs text-[#555] truncate">{r.email}</p>
              </div>
              <span className="label-eyebrow text-[#A87900] flex-shrink-0">
                {r.missing_count} listing{r.missing_count === 1 ? "" : "s"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const StatCard = ({ label, value, hint, accent = false }) => (
  <div
    className={`border p-6 ${accent ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[#E5E0D8] bg-white"}`}
  >
    <p className={`label-eyebrow ${accent ? "text-[#F4C430]" : "text-[#1E3A8A]"}`}>{label}</p>
    <p className={`font-mono-aam text-4xl mt-3 ${accent ? "text-white" : "text-[#111]"}`}>
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
    {hint && (
      <p className={`text-xs mt-2 ${accent ? "text-white/80" : "text-[#555]"}`}>{hint}</p>
    )}
  </div>
);

function TopSocialPosts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/admin/social/top-posts", { params: { days: 30, limit: 5 } })
      .then(({ data }) => setData(data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;

  return (
    <section data-testid="admin-top-social-posts" className="mt-12 border border-[#E5E0D8] bg-white">
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#E5E0D8]">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-4 h-4 text-[#1E3A8A]" />
          <div>
            <p className="label-eyebrow text-[#1E3A8A]">— Cross-platform traction</p>
            <h2 className="font-serif-display text-xl tracking-tight font-medium mt-1 text-[#111]">
              Top social posts · last 30 days
            </h2>
          </div>
        </div>
        <Link
          to="/admin/social"
          className="label-eyebrow text-[#1E3A8A] hover:text-[#111]"
          data-testid="top-social-all-link"
        >
          All posts →
        </Link>
      </header>
      {!data ? (
        <p className="px-6 py-8 label-eyebrow text-[#555]">Loading…</p>
      ) : data.items.length === 0 ? (
        <p className="px-6 py-8 text-sm text-[#555]" data-testid="top-social-empty">
          No engagement data yet. Once your social posts pick up likes, shares or comments, the top performers will surface here.
        </p>
      ) : (
        <ol className="divide-y divide-[#E5E0D8]">
          {data.items.map((it, idx) => (
            <li
              key={`${it.target_kind}-${it.target_id}`}
              data-testid={`top-social-row-${idx}`}
              className="px-6 py-4 flex items-center gap-4"
            >
              <span className="font-mono-aam text-2xl text-[#1E3A8A] w-8 flex-shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </span>
              {it.image && (
                <img
                  src={it.image}
                  alt=""
                  className="w-14 h-14 object-cover border border-[#E5E0D8] flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="label-eyebrow text-[#555]">
                    {it.target_kind === "artwork" ? "Artwork" : it.target_kind === "event" ? "Event" : "Article"}
                  </span>
                  <span className="text-[#E5E0D8]">·</span>
                  <span className="label-eyebrow text-[#1B5E20]">
                    {it.platforms.map((p) => PLATFORM_LABEL[p] || p).join(" · ")}
                  </span>
                </div>
                {it.href ? (
                  <Link
                    to={it.href}
                    className="block font-serif-display text-lg tracking-tight mt-1 text-[#111] hover:text-[#1E3A8A] truncate"
                  >
                    {it.title}
                  </Link>
                ) : (
                  <p className="font-serif-display text-lg tracking-tight mt-1 text-[#111] truncate">
                    {it.title}
                  </p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-mono-aam text-2xl text-[#111]">{it.score.toLocaleString()}</p>
                <p className="label-eyebrow text-[#555]">engagements</p>
              </div>
              {it.best_post_url && (
                <a
                  href={it.best_post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`top-social-link-${idx}`}
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1 flex-shrink-0"
                  title={`Open on ${PLATFORM_LABEL[it.best_platform] || it.best_platform}`}
                >
                  Open <ArrowUpRight className="w-3 h-3" />
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then(({ data }) => setStats(data))
      .catch(() => toast.error("Could not load admin stats"));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <p className="label-eyebrow text-[#1E3A8A]">— Overview</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Dashboard
      </h1>
      <p className="text-[#555] mt-3 max-w-xl">
        Real-time platform health. Numbers update every visit to this page.
      </p>

      {stats && stats.pending_approvals > 0 && (
        <Link
          to="/admin/users?approval=pending"
          data-testid="pending-approvals-banner"
          className="mt-8 block border-2 border-[#F4C430] bg-[#F4C430]/15 px-6 py-4 hover:bg-[#F4C430]/25 transition-colors"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="label-eyebrow text-[#1E3A8A]">— Action required</p>
              <p className="font-serif-display text-2xl mt-1 text-[#111]">
                {stats.pending_approvals} account{stats.pending_approvals === 1 ? "" : "s"} awaiting review
              </p>
            </div>
            <span className="label-eyebrow text-[#1E3A8A]">Review now →</span>
          </div>
        </Link>
      )}

      {!stats ? (
        <p className="mt-10 label-eyebrow text-[#555]">Loading…</p>
      ) : (
        <>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total revenue"
              value={`$${stats.revenue_usd.toLocaleString()}`}
              hint={`${stats.paid_orders} paid orders`}
              accent
            />
            <StatCard label="Total users" value={stats.total_users} hint={`${stats.artists} artists · ${stats.buyers} buyers`} />
            <StatCard label="Artworks listed" value={stats.artworks} hint={`${stats.sold_artworks} sold`} />
            <StatCard label="Live auctions" value={stats.live_auctions} hint={`${stats.sold_auctions} sold via auction`} />
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Pending orders" value={stats.pending_orders} hint="Awaiting payment" />
            <StatCard label="Uploads stored" value={stats.uploads} hint="Object storage" />
            <StatCard label="Sold artworks" value={stats.sold_artworks} hint={`${((stats.sold_artworks / Math.max(stats.artworks, 1)) * 100).toFixed(0)}% of catalogue`} />
            <StatCard label="Artist accounts" value={stats.artists} hint={`${stats.buyers} collectors`} />
          </div>

          <TopSocialPosts />
          <ShippingReminderCard />
        </>
      )}
    </div>
  );
}
